import {
  Box,
  Stack,
  Typography,
  Divider,
  styled,
  Chip,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import { formatCurrency } from "@/utils/format-currency";
import { formatLabel } from "@/utils/format-label";
import { DataTable } from "@/components/custom/data-table";
import { CustomButton } from "@/components/custom/custom-button";
import CustomSearchInput from "@/components/custom/custom-search-input";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { getGlLedgerColumns } from "./_components/gl-ledger-column";
import CustomBackButton from "@/components/custom/custom-back-button";
import { useGlAccountWithBalance } from "@/hooks/use-gl-account-with-balance";
import { useGlAccountLedger } from "@/hooks/use-gl-account-ledger";
import CustomDateRangeInput from "@/components/custom/custom-date-range-input";

const RootWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
}));

const StatBox = styled(Box)(({ theme }) => ({
  flex: 1,
  borderRadius: 12,
  padding: "20px 24px",
  minHeight: 100,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
}));

const GlAccountDetail = () => {
  const { glId } = useParams();
  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";
  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);
  const fromDate = searchParams.get("fromDate");
  const toDate = searchParams.get("toDate");
  const direction = searchParams.get("direction") || "";

  const { data: response, isLoading } = useGlAccountWithBalance(glId, true);

  const queryParams = {
    page: currentPage,
    size: pageSize,
    search: searchValue || undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    direction: direction || undefined,
  };

  const {
    data: ledger,
    isLoading: ledgerLoading,
    isError: ledgerError,
  } = useGlAccountLedger(glId, true, queryParams);

  const account = response?.data?.account ?? null;
  const balance = response?.data?.balance ?? null;

  if (isLoading) {
    return (
      <RootWrapper>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CircularProgress size={16} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Stack>
      </RootWrapper>
    );
  }

  if (!account) {
    return (
      <Typography color="text.secondary" p={4}>
        GL account not found.
      </Typography>
    );
  }

  const isActive = account.isActive;

  return (
    <RootWrapper>
      <Stack direction="row" alignItems="center" spacing={2}>
        <CustomBackButton />
        <Typography variant="h6" fontWeight={600}>
          {account.name}
        </Typography>
        <Chip
          label={isActive ? "Active" : "Inactive"}
          size="small"
          sx={{
            borderRadius: "999px",
            fontWeight: 500,
            fontSize: "0.75rem",
            backgroundColor: isActive ? "#ecfdf5" : "#fef2f2",
            color: isActive ? "#065f46" : "#991b1b",
          }}
        />
      </Stack>

      <Card>
        <Stack direction="row" spacing={2} mb={2}>
          <StatBox>
            <Typography variant="caption" color="text.secondary">
              Total Debits
            </Typography>

            <Typography fontSize="1.1rem" fontWeight={600} mt={0.5}>
              {balance?.debitTotal != null
                ? formatCurrency(balance.debitTotal, balance.currency || "NGN")
                : "—"}
            </Typography>
          </StatBox>

          <StatBox>
            <Typography variant="caption" color="text.secondary">
              Total Credits
            </Typography>

            <Typography fontSize="1.1rem" fontWeight={600} mt={0.5}>
              {balance?.creditTotal != null
                ? formatCurrency(balance.creditTotal, balance.currency || "NGN")
                : "—"}
            </Typography>
          </StatBox>

          <StatBox>
            <Typography variant="caption" color="text.secondary">
              Net Balance
            </Typography>

            <Typography fontSize="1.1rem" fontWeight={600} mt={0.5}>
              {balance?.balance != null
                ? formatCurrency(balance.balance, balance.currency || "NGN")
                : "—"}
            </Typography>
          </StatBox>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          {[
            { label: "Account Code", value: account.code },
            { label: "Account Name", value: account.name },
            { label: "Account Type", value: formatLabel(account.accountType) },
            { label: "Category", value: formatLabel(account.category) },
            {
              label: "Normal Balance",
              value: formatLabel(account.normalBalance),
            },
            { label: "Currency", value: account.currency },
            {
              label: "Control Account",
              value: account.isControlAccount ? "Yes" : "No",
            },
            { label: "Postable", value: account.isPostable ? "Yes" : "No" },
            { label: "Created By", value: account.createdBy },
            { label: "Description", value: account.description },
          ].map(({ label, value }) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {label}
                </Typography>

                <Typography fontSize="0.875rem" fontWeight={500} mt={0.25}>
                  {value ?? "N/A"}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>

      <Card>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography
            fontSize="0.75rem"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing="0.05em"
            color="text.secondary"
          >
            Account Ledger
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <CustomButton
              variantType="outline"
              endIcon={<PrintOutlinedIcon fontSize="small" />}
              onClick={() => window.print()}
              sx={{ height: 35 }}
            >
              Print
            </CustomButton>
            <CustomButton
              variantType="outline"
              endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
              onClick={() => {}}
              sx={{ height: 35 }}
            >
              Export
            </CustomButton>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} mb={2}>
          <CustomSearchInput
            paramKey="search"
            placeholder="Search by ledger no. or description..."
          />
          <CustomDateRangeInput fromKey="fromDate" toKey="toDate" />
        </Stack>

        <DataTable
          data={ledger?.data?.data ?? []}
          columns={getGlLedgerColumns()}
          isLoading={ledgerLoading}
          isError={ledgerError}
        />
      </Card>
    </RootWrapper>
  );
};

export default GlAccountDetail;
